import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Send, Archive, Trash2, ArrowLeft, Clock, User, FolderOpen, Flag, Paperclip, Download, FileText, X, Tag, CheckSquare, Square, Plus } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Open: "bg-sky-50 text-sky-700 border-sky-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Waiting: "bg-orange-50 text-orange-700 border-orange-200",
  Completed: "bg-green-50 text-green-700 border-green-200",
  Archived: "bg-gray-100 text-gray-500 border-gray-200",
};

const PRIORITY_COLORS = {
  Low: "text-gray-500", Normal: "text-blue-500", High: "text-orange-500", Urgent: "text-red-500",
};

export default function TaskDetailPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [flags, setFlags] = useState([]);
  const [editing, setEditing] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [newCheckItem, setNewCheckItem] = useState("");
  const fileInputRef = useRef(null);
  const msgEnd = useRef(null);

  useEffect(() => {
    loadTask();
    API.get(`/tasks/${taskId}/messages`).then(r => setMessages(r.data)).catch(() => {});
    API.get(`/tasks/${taskId}/activity`).then(r => setActivity(r.data)).catch(() => {});
    API.get(`/tasks/${taskId}/attachments`).then(r => setAttachments(r.data)).catch(() => {});
    API.get(`/tasks/${taskId}/checklist`).then(r => setChecklist(r.data)).catch(() => {});
    API.get("/users").then(r => setUsers(r.data)).catch(() => {});
    API.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    API.get("/settings/flags").then(r => setFlags(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const loadTask = () => API.get(`/tasks/${taskId}`).then(r => setTask(r.data)).catch((err) => {
    if (err.response?.status === 403) {
      toast.error("Completed tasks are only visible to the original creator");
    }
    navigate("/tasks");
  });

  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const updateField = async (field, value) => {
    try {
      const { data } = await API.put(`/tasks/${taskId}`, { [field]: value });
      setTask(data);
      toast.success("Task updated");
      API.get(`/tasks/${taskId}/activity`).then(r => setActivity(r.data));
    } catch { toast.error("Update failed"); }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const { data } = await API.post(`/tasks/${taskId}/messages`, { content: newMessage });
      setMessages(m => [...m, data]);
      setNewMessage("");
    } catch { toast.error("Failed to send message"); }
  };

  const handleArchive = async () => {
    await API.post(`/tasks/${taskId}/archive`);
    toast.success("Task archived");
    navigate("/tasks");
  };

  const handleDelete = async () => {
    await API.delete(`/tasks/${taskId}`);
    toast.success("Task deleted");
    navigate("/tasks");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await API.post(`/tasks/${taskId}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments(a => [data, ...a]);
      toast.success(`File "${file.name}" attached`);
      API.get(`/tasks/${taskId}/activity`).then(r => setActivity(r.data));
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownload = async (att) => {
    try {
      const response = await API.get(`/files/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDeleteAttachment = async (att) => {
    try {
      await API.delete(`/files/${att.id}`);
      setAttachments(a => a.filter(f => f.id !== att.id));
      toast.success("Attachment removed");
      API.get(`/tasks/${taskId}/activity`).then(r => setActivity(r.data));
    } catch {
      toast.error("Failed to remove");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const addCheckItem = async () => {
    if (!newCheckItem.trim()) return;
    try {
      const { data } = await API.post(`/tasks/${taskId}/checklist`, { text: newCheckItem });
      setChecklist(c => [...c, data]);
      setNewCheckItem("");
    } catch { toast.error("Failed to add item"); }
  };

  const toggleCheckItem = async (item) => {
    try {
      const { data } = await API.put(`/tasks/${taskId}/checklist/${item.id}`, { done: !item.done });
      setChecklist(c => c.map(i => i.id === item.id ? data : i));
    } catch { toast.error("Failed to update"); }
  };

  const deleteCheckItem = async (itemId) => {
    try {
      await API.delete(`/tasks/${taskId}/checklist/${itemId}`);
      setChecklist(c => c.filter(i => i.id !== itemId));
    } catch { toast.error("Failed to delete"); }
  };

  const checklistDone = checklist.filter(i => i.done).length;
  const checklistTotal = checklist.length;

  if (!task) return <div className="flex-1 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#111216] border-t-transparent" /></div>;

  return (
    <>
      <TopBar title={task.task_number} />
      <div className="flex-1 overflow-y-auto" data-testid="task-detail-page">
        {/* Header */}
        <div className="px-6 py-4 bg-white border-b border-[#EBEBEB]">
          <div className="flex items-center gap-3 mb-3">
            <button data-testid="task-back-btn" onClick={() => navigate("/tasks")} className="text-[#686B74] hover:text-[#111216] transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-mono text-[#686B74]">{task.task_number}</span>
            <Badge variant="outline" className={`text-[11px] ${STATUS_COLORS[task.status]}`}>{task.status}</Badge>
            {task.flag_name && (
              <span
                data-testid="task-flag-badge"
                className="text-[11px] font-medium px-2 py-0.5 rounded-full border"
                style={{ backgroundColor: task.flag_color + "20", color: task.flag_color, borderColor: task.flag_color + "40" }}
              >
                {task.flag_name}
              </span>
            )}
          </div>
          {editing.title ? (
            <Input
              autoFocus
              data-testid="task-title-input"
              defaultValue={task.title}
              onBlur={e => { updateField("title", e.target.value); setEditing({}); }}
              onKeyDown={e => { if (e.key === "Enter") { updateField("title", e.target.value); setEditing({}); } }}
              className="text-xl font-bold font-['Manrope']"
            />
          ) : (
            <h1
              data-testid="task-title"
              className="text-xl font-bold font-['Manrope'] text-[#111216] cursor-pointer hover:text-[#4353FF] transition-colors"
              onClick={() => setEditing({ title: true })}
            >
              {task.title}
            </h1>
          )}
          <div className="flex items-center gap-2 mt-2">
            {/* WORKFLOW: Only creator can archive/delete, and only when task is completed */}
            {task.status === "Completed" && task.creator_id === user?.id && (
              <>
                <Button data-testid="task-archive-btn" variant="outline" size="sm" onClick={handleArchive} className="h-7 text-xs">
                  <Archive className="w-3 h-3 mr-1" /> Archive
                </Button>
                <Button data-testid="task-delete-btn" variant="outline" size="sm" onClick={handleDelete} className="h-7 text-xs text-[#E24A4A] hover:text-[#E24A4A]">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </>
            )}
            {/* Archived tasks: creator can delete */}
            {task.status === "Archived" && task.creator_id === user?.id && (
              <Button data-testid="task-delete-btn" variant="outline" size="sm" onClick={handleDelete} className="h-7 text-xs text-[#E24A4A] hover:text-[#E24A4A]">
                <Trash2 className="w-3 h-3 mr-1" /> Delete Permanently
              </Button>
            )}
            {task.status !== "Completed" && task.status !== "Archived" && (
              <span className="text-[11px] text-[#686B74]">Complete this task to archive or delete it</span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row">
          {/* Main - 70% */}
          <div className="flex-1 lg:w-[70%] p-6 space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-xs font-medium text-[#686B74] uppercase tracking-wider mb-2">Description</h3>
              <Textarea
                data-testid="task-description"
                value={task.description || ""}
                onChange={e => setTask(t => ({ ...t, description: e.target.value }))}
                onBlur={e => updateField("description", e.target.value)}
                placeholder="Add a description..."
                className="min-h-[80px] text-sm"
              />
            </div>
            {/* Notes */}
            <div>
              <h3 className="text-xs font-medium text-[#686B74] uppercase tracking-wider mb-2">Notes</h3>
              <Textarea
                data-testid="task-notes"
                value={task.notes || ""}
                onChange={e => setTask(t => ({ ...t, notes: e.target.value }))}
                onBlur={e => updateField("notes", e.target.value)}
                placeholder="Add notes..."
                className="min-h-[60px] text-sm"
              />
            </div>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-medium text-[#686B74] uppercase tracking-wider flex items-center gap-1.5">
                  <CheckSquare className="w-3 h-3" /> Checklist
                  {checklistTotal > 0 && (
                    <span className="text-[10px] font-normal normal-case tracking-normal ml-1 text-[#686B74]">
                      {checklistDone}/{checklistTotal}
                    </span>
                  )}
                </h3>
                {checklistTotal > 0 && (
                  <div className="w-20 h-1.5 rounded-full bg-[#EBEBEB] overflow-hidden">
                    <div className="h-full rounded-full bg-[#34B56F] transition-all" style={{ width: `${checklistTotal > 0 ? (checklistDone / checklistTotal) * 100 : 0}%` }} />
                  </div>
                )}
              </div>
              <div className="bg-white border border-[#EBEBEB] rounded-md">
                <div className="divide-y divide-[#EBEBEB]">
                  {checklist.map((item) => (
                    <div key={item.id} className="flex items-center gap-2 px-3 py-2 group" data-testid={`checklist-item-${item.id}`}>
                      <button onClick={() => toggleCheckItem(item)} className="flex-shrink-0" data-testid={`checklist-toggle-${item.id}`}>
                        {item.done ? (
                          <CheckSquare className="w-4 h-4 text-[#34B56F]" />
                        ) : (
                          <Square className="w-4 h-4 text-[#686B74] hover:text-[#111216]" />
                        )}
                      </button>
                      <span className={`flex-1 text-sm ${item.done ? "line-through text-[#686B74]" : "text-[#111216]"}`}>
                        {item.text}
                      </span>
                      <button
                        onClick={() => deleteCheckItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-[#686B74] hover:text-[#E24A4A] transition-all"
                        data-testid={`checklist-delete-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 px-3 py-2 border-t border-[#EBEBEB]">
                  <Input
                    data-testid="checklist-input"
                    value={newCheckItem}
                    onChange={e => setNewCheckItem(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addCheckItem(); }}
                    placeholder="Add checklist item..."
                    className="flex-1 h-7 text-sm border-0 shadow-none focus-visible:ring-0 px-0"
                  />
                  <button onClick={addCheckItem} className="text-[#686B74] hover:text-[#111216] transition-colors" data-testid="checklist-add-btn">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages & Activity & Attachments */}
            <Tabs defaultValue="messages" className="w-full">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
                <TabsTrigger value="attachments" data-testid="tab-attachments">
                  Attachments {attachments.length > 0 && `(${attachments.length})`}
                </TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
              </TabsList>
              <TabsContent value="messages">
                <div className="bg-white border border-[#EBEBEB] rounded-md">
                  <div className="max-h-80 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 && <p className="text-sm text-[#686B74] text-center py-4">No messages yet</p>}
                    {messages.map((m) => (
                      <div key={m.id} className="flex gap-3" data-testid={`message-${m.id}`}>
                        <Avatar className="h-7 w-7 flex-shrink-0">
                          <AvatarImage src={m.user_avatar} />
                          <AvatarFallback className="text-[10px] bg-[#111216] text-white">{m.user_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{m.user_name}</span>
                            <span className="text-[11px] text-[#686B74]">{m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true }) : ""}</span>
                          </div>
                          <div className="mt-1 text-sm text-[#111216] bg-[#F9F9FB] rounded-md px-3 py-2">{m.content}</div>
                        </div>
                      </div>
                    ))}
                    <div ref={msgEnd} />
                  </div>
                  <div className="border-t border-[#EBEBEB] p-3 flex gap-2">
                    <Input
                      data-testid="message-input"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      placeholder="Write a message..."
                      className="flex-1 h-9 text-sm"
                    />
                    <Button data-testid="message-send-btn" onClick={sendMessage} size="sm" className="h-9 bg-[#111216] hover:bg-[#2B2D31]">
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="attachments">
                <div className="bg-white border border-[#EBEBEB] rounded-md">
                  {/* Upload button */}
                  <div className="p-3 border-b border-[#EBEBEB]">
                    <input ref={fileInputRef} type="file" onChange={handleFileUpload} className="hidden" />
                    <Button
                      data-testid="upload-attachment-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="text-xs"
                    >
                      <Paperclip className="w-3.5 h-3.5 mr-1.5" />
                      {uploading ? "Uploading..." : "Attach File"}
                    </Button>
                  </div>
                  {/* File list */}
                  <div className="max-h-80 overflow-y-auto divide-y divide-[#EBEBEB]">
                    {attachments.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-[#686B74]">No attachments yet</div>
                    )}
                    {attachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#F9F9FB] transition-colors" data-testid={`attachment-${att.id}`}>
                        <FileText className="w-4 h-4 text-[#686B74] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#111216] truncate">{att.original_filename}</p>
                          <p className="text-[11px] text-[#686B74]">
                            {formatFileSize(att.size)} &middot; {att.uploaded_by_name} &middot; {att.created_at ? formatDistanceToNow(new Date(att.created_at), { addSuffix: true }) : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            data-testid={`download-${att.id}`}
                            onClick={() => handleDownload(att)}
                            className="p-1.5 text-[#686B74] hover:text-[#4353FF] transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`delete-attachment-${att.id}`}
                            onClick={() => handleDeleteAttachment(att)}
                            className="p-1.5 text-[#686B74] hover:text-[#E24A4A] transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="activity">
                <div className="bg-white border border-[#EBEBEB] rounded-md max-h-96 overflow-y-auto">
                  {activity.length === 0 && <p className="text-sm text-[#686B74] text-center py-8">No activity yet</p>}
                  {activity.map((a) => (
                    <div key={a.id} className="px-4 py-3 border-b border-[#EBEBEB] last:border-0" data-testid={`activity-${a.id}`}>
                      <p className="text-sm"><span className="font-medium">{a.user_name}</span> <span className="text-[#686B74]">{a.details || a.action_type}</span></p>
                      <p className="text-[11px] text-[#686B74] mt-0.5">{a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ""}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - 30% */}
          <div className="lg:w-[30%] border-l border-[#EBEBEB] bg-white p-6 space-y-5">
            {/* Status */}
            <div>
              <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                <Clock className="w-3 h-3" /> Status
              </div>
              <Select value={task.status} onValueChange={v => updateField("status", v)}>
                <SelectTrigger data-testid="task-status-select" className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["New", "Open", "In Progress", "Waiting", "Completed"].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Priority */}
            <div>
              <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                <Flag className={`w-3 h-3 ${PRIORITY_COLORS[task.priority]}`} /> Priority
              </div>
              <Select value={task.priority} onValueChange={v => updateField("priority", v)}>
                <SelectTrigger data-testid="task-priority-select" className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Low", "Normal", "High", "Urgent"].map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Flag */}
            {flags.length > 0 && (user?.id === task.creator_id || user?.id === task.owner_id) && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                  <Tag className="w-3 h-3" /> Flag
                </div>
                <Select value={task.flag_id || "none"} onValueChange={v => updateField("flag_id", v === "none" ? null : v)}>
                  <SelectTrigger data-testid="task-flag-select" className="h-8 text-sm">
                    <SelectValue placeholder="No flag" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No flag</SelectItem>
                    {flags.map(f => (
                      <SelectItem key={f.id} value={f.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: f.color }} />
                          {f.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {flags.length > 0 && user?.id !== task.creator_id && user?.id !== task.owner_id && task.flag_name && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                  <Tag className="w-3 h-3" /> Flag
                </div>
                <span
                  className="text-xs font-medium px-2.5 py-1 rounded-full border inline-block"
                  style={{ backgroundColor: task.flag_color + "20", color: task.flag_color, borderColor: task.flag_color + "40" }}
                >
                  {task.flag_name}
                </span>
              </div>
            )}
            {/* Owner */}
            <div>
              <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                <User className="w-3 h-3" /> Owner
              </div>
              <Select value={task.owner_id || ""} onValueChange={v => updateField("owner_id", v)}>
                <SelectTrigger data-testid="task-owner-select" className="h-8 text-sm">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.status === "active").map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Group */}
            <div>
              <div className="flex items-center gap-2 mb-2 text-xs font-medium text-[#686B74] uppercase tracking-wider">
                <FolderOpen className="w-3 h-3" /> Group
              </div>
              <Select value={task.group_id || "none"} onValueChange={v => updateField("group_id", v === "none" ? null : v)}>
                <SelectTrigger data-testid="task-group-select" className="h-8 text-sm">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Due Date */}
            <div>
              <div className="text-xs font-medium text-[#686B74] uppercase tracking-wider mb-2">Due Date</div>
              <Input
                data-testid="task-due-date"
                type="date"
                value={task.due_date || ""}
                onChange={e => updateField("due_date", e.target.value || null)}
                className="h-8 text-sm"
              />
            </div>
            {/* Meta */}
            <div className="pt-4 border-t border-[#EBEBEB] space-y-3 text-xs text-[#686B74]">
              <div className="flex justify-between"><span>Creator</span><span className="text-[#111216]">{task.creator_name}</span></div>
              <div className="flex justify-between"><span>Created</span><span>{task.created_at ? format(new Date(task.created_at), "MMM d, yyyy") : "-"}</span></div>
              <div className="flex justify-between"><span>Updated</span><span>{task.updated_at ? format(new Date(task.updated_at), "MMM d, yyyy") : "-"}</span></div>
              {task.completed_by_name && (
                <div className="flex justify-between"><span>Completed By</span><span className="text-[#34B56F]">{task.completed_by_name}</span></div>
              )}
              {task.completed_date && (
                <div className="flex justify-between"><span>Completed</span><span>{format(new Date(task.completed_date), "MMM d, yyyy")}</span></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
